import React, { useEffect, useState } from "react";

import { useForm } from "react-hook-form/dist/react-hook-form.ie11";
import { useHistory, useParams } from "react-router-dom";

import { fireOnce, regexTester } from "@ds-developer/frontend";
import { objectKeys } from "@ds-developer/frontend";

import { Brand } from "@hubcapp/graphql";
import { getBrandRoute } from "@hubcapp/utils";
import { useAuth, useTealium } from "@hubcapp/context";

import {
  SetPasswordFormFields,
  SetPasswordProps,
  StateProviderProps,
} from "./types";
import SetPassword from "./SetPassword";
import validationSchema from "./validationSchema";

const SetPasswordStateProvider: React.FC<StateProviderProps> = ({
  initialFormFields,
}) => {
  const { push } = useHistory();
  const { trackEvent } = useTealium();
  const { brand } = useParams<{ brand: Brand }>();
  const {
    instructor: { adiNumber },
    resendMFAToResetPassword,
    resetPassword,
  } = useAuth();

  if (!adiNumber) {
    // user ID is required to authenticate
    push(`/${getBrandRoute(brand)}/sign-in`);
  }

  const {
    control,
    errors,
    formState: { isValid },
    handleSubmit,
    register,
    triggerValidation,
    watch,
  } = useForm<SetPasswordFormFields>({
    defaultValues: initialFormFields,
    mode: "onChange",
    validationSchema,
  });

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(
    false,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [resendLoading, setResendLoading] = useState<boolean>(false);
  const [primaryErrorMessage, setPrimaryErrorMessage] = useState<string>("");

  const [resendStatus, setResendState] = useState<
    SetPasswordProps["resendStatus"]
  >(undefined);

  const [showResendButton, setShowResendButton] = useState(false);

  const handleResendClick = () => {
    trackEvent({
      eventCategory: "Access Code",
      eventAction: "Link Click",
      eventLabel: "Resend It",
    });
    setResendLoading(true);
    setShowResendButton(false);

    resendMFAToResetPassword()
      .then(() => {
        setResendLoading(false);
        setResendState("SUCCESS");
        setTimeout(() => setShowResendButton(true), 30000);
      })
      .catch(() => {
        setResendState("FAILURE");
        setShowResendButton(true);
      });
  };

  const submit = ({ accessCode, password }: SetPasswordFormFields) => {
    trackEvent({
      eventCategory: "Access Code",
      eventAction: "Button Click",
      eventLabel: "Verify",
    });
    setLoading(true);

    resetPassword(accessCode, password)
      .then(() => push(`/${getBrandRoute(brand)}/sign-in`))
      .catch(() => {
        setLoading(false);
        setPrimaryErrorMessage(
          "You have entered an incorrect access code. Please try again.",
        );
      });
  };

  const setInitialValidation = () => {
    if (initialFormFields) {
      triggerValidation(objectKeys(initialFormFields));
    }
  };

  const handleDigitCodeEvent = () => {
    fireOnce("Access Code|Interaction|Digit Code", () => {
      trackEvent({
        eventCategory: "Access Code",
        eventAction: "Interaction",
        eventLabel: "Digit Code",
      });
    });
  };

  const handleEnterPasswordEvent = () => {
    fireOnce("Set Password|Interaction|Enter New Password", () => {
      trackEvent({
        eventCategory: "Set Password",
        eventAction: "Interaction",
        eventLabel: "Enter New Password",
      });
    });
  };

  const handleConfirmPasswordEvent = () => {
    fireOnce("Set Password|Interaction|Confirm New Password", () => {
      trackEvent({
        eventCategory: "Set Password",
        eventAction: "Interaction",
        eventLabel: "Confirm New Password",
      });
    });
  };

  const handleSetPasswordEvent = () => {
    trackEvent({
      eventCategory: "Set Password",
      eventAction: "Button Click",
      eventLabel: "Set New Password",
    });
  };

  useEffect(setInitialValidation, []);

  useEffect(() => {
    setTimeout(() => setShowResendButton(true), 30000);
  }, []);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const password: string | undefined = watch("password");
  const hasCapital = regexTester(password, /.*[A-Z].*/);
  const hasNumber = regexTester(password, /.*[0-9].*/);
  const isLongerThan8Chars = (password?.length || 0) >= 8;

  const handleDigitCodeErrorEvent = () => {
    fireOnce(
      `Set Password|Form Field Error|${errors?.accessCode?.message}`,
      () => {
        trackEvent({
          eventCategory: "Set Password",
          eventAction: "Form Field Error",
          eventLabel: `${errors?.accessCode?.message}`,
        });
      },
    );
  };

  const handleEnterPasswordErrorEvent = () => {
    fireOnce(
      `Set Password|Form Field Error|${errors?.password?.message}`,
      () => {
        trackEvent({
          eventCategory: "Set Password",
          eventAction: "Form Field Error",
          eventLabel: `${errors?.password?.message}`,
        });
      },
    );
  };

  const handleConfirmPasswordErrorEvent = () => {
    fireOnce(
      `Set Password|Form Field Error|${errors?.confirmPassword?.message}`,
      () => {
        trackEvent({
          eventCategory: "Set Password",
          eventAction: "Form Field Error",
          eventLabel: `${errors?.confirmPassword?.message}`,
        });
      },
    );
  };

  return (
    <SetPassword
      control={control}
      errors={errors}
      isLoading={loading}
      isValid={isValid}
      onResendClicked={handleResendClick}
      onSubmit={handleSubmit(submit)}
      handleDigitCodeEvent={handleDigitCodeEvent}
      resendLoading={resendLoading}
      hasCapital={hasCapital}
      hasNumber={hasNumber}
      isLongerThan8Chars={isLongerThan8Chars}
      register={register}
      handleClickShowConfirmPassword={handleClickShowConfirmPassword}
      handleClickShowPassword={handleClickShowPassword}
      showConfirmPassword={showConfirmPassword}
      showPassword={showPassword}
      resendStatus={resendStatus}
      handleSetPasswordEvent={handleSetPasswordEvent}
      handleEnterPasswordEvent={handleEnterPasswordEvent}
      handleConfirmPasswordEvent={handleConfirmPasswordEvent}
      handleConfirmPasswordErrorEvent={handleConfirmPasswordErrorEvent}
      handleEnterPasswordErrorEvent={handleEnterPasswordErrorEvent}
      handleDigitCodeErrorEvent={handleDigitCodeErrorEvent}
      primaryErrorMessage={primaryErrorMessage}
      showResendButton={showResendButton}
    />
  );
};

export default SetPasswordStateProvider;
